import React, { useState, useEffect } from "react";
import { fetchOpenAIText } from "../utils/openaiClient";

const TEMPLATES = [
  { label: "Create ABCD Questions for a Text", prompt: "Create a list of ABCD questions for the following text. Only one correct answer per question." },
  { label: "Create Open Questions for a Text", prompt: "Create a list of open questions for the following text." },
  { label: "Word-Definition Matching", prompt: "Create a matching exercise for these words: match each word to its meaning." },
  { label: "Fill in the Gap", prompt: "Replace the chosen words in the text with gaps and give a list of these words separately." },
  { label: "Create True/False Statements for a Text", prompt: "Create a true/false reading exercise for the following text." },
  { label: "Warm-Up Discussion Questions", prompt: "Create warm-up discussion questions for the following topic or text." },
  { label: "Create a Dialogue on Any Topic", prompt: "Create a dialogue based on the following text or topic." },
  { label: "Essential Vocabulary on a Topic", prompt: "Suggest a list of essential vocabulary items for the following topic." },
  { label: "Lead-in Activities for a Text", prompt: "Create 3 lead-in activity ideas for the following text." },
  { label: "Four Opinions", prompt: "Create four opinions by different people on the following topic." },
  { label: "Create Communicative Situations with Your Vocabulary", prompt: "Create short two-line dialogues with the target vocabulary." },
  { label: "Word-Translation Matching", prompt: "Translate these words/phrases into different languages and create a matching exercise." },
  { label: "Create a List of Pros and Cons", prompt: "Create a list of pros and cons on the following topic." },
  { label: "Create an Exercise with Matching Halves", prompt: "Create an exercise where students match halves of collocations, sentences, etc." },
  { label: "Error Correction Exercise", prompt: "Provide sentences with mistakes on the chosen grammar topic for correction." },
  { label: "Grammar Rules", prompt: "Produce customized grammar rules and explanations for the following topic." },
];


export default function AIStarAssistantModal({ onClose, editor, onInsertToBoard, currentTab = 0 }) {
  const [template, setTemplate] = useState(TEMPLATES[0]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [level, setLevel] = useState("B1");
  const [exerciseData, setExerciseData] = useState(null);
  const [phrasesInput, setPhrasesInput] = useState("");
  const [gapLevel, setGapLevel] = useState("B1");

  const handleGenerate = async () => {
    setLoading(true);
    setResult("");
    setError("");
    setExerciseData(null);
    
    try {
      let prompt;
      if (template.label === "Fill in the Gap") {
        // Парсим фразы (через запятую или перенос строки)
        const phrases = phrasesInput
          .split(/,|\n/)
          .map(p => p.trim())
          .filter(Boolean);
        prompt = `For each of the following words or phrases, generate a separate English sentence (not a story, not a single text, just independent sentences). The sentence should be at CEFR level ${gapLevel} (the higher the level, the more complex and longer the sentence). Insert the word/phrase into the sentence and highlight it with double asterisks (e.g. **word**). Do NOT use ___ or any other gap marker. Use ONLY the words/phrases provided, do not add or change anything. Return only valid JSON in this format:\n{\n  "text": "1. ... 2. ... 3. ...",\n  "gaps": [\n    {"id": "gap_1", "word": "original_word_or_phrase", "start": 0, "end": 0}\n  ]\n}\nWords/phrases:\n${phrases.join("\n")}`;
        setInput(phrases.join("\n")); // для истории
      } else if (template.label === "Create ABCD Questions for a Text") {
        prompt = `Create an interactive ABCD exercise for the following text. Generate 3-5 questions with 4 options each. Only one correct answer per question. The level should be ${level}.

IMPORTANT: Return ONLY valid JSON in this exact format:
{
  "title": "Exercise Title",
  "level": "${level}",
  "questions": [
    {
      "question": "Question text here?",
      "options": [
        {"id": "a", "text": "Option A text", "isCorrect": false},
        {"id": "b", "text": "Option B text", "isCorrect": true},
        {"id": "c", "text": "Option C text", "isCorrect": false},
        {"id": "d", "text": "Option D text", "isCorrect": false}
      ]
    }
  ]
}

Text: ${input}`;
      } else {
        prompt = `${template.prompt}\n\n${input}`;
      }
      
      console.log('Sending prompt:', prompt);
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 800,
      });
      console.log('OpenAI content:', content);
      if (content) {
        setResult(content);
        
        // Try to parse as JSON for ABCD exercises
        if (template.label === "Create ABCD Questions for a Text") {
          try {
            const jsonData = JSON.parse(content);
            console.log('Parsed JSON data:', jsonData);
            setExerciseData(jsonData);
          } catch (e) {
            console.error("Failed to parse ABCD exercise JSON:", e);
            console.error("Raw content that failed to parse:", content);
            
            // Fallback to test data for development
            const fallbackData = {
              title: "Sample ABCD Exercise",
              level: level,
              questions: [
                {
                  question: "What is the main topic of the text?",
                  options: [
                    { id: "a", text: "Technology", isCorrect: false },
                    { id: "b", text: "Education", isCorrect: true },
                    { id: "c", text: "Sports", isCorrect: false },
                    { id: "d", text: "Food", isCorrect: false }
                  ]
                },
                {
                  question: "Which of the following statements is true?",
                  options: [
                    { id: "a", text: "The text discusses ancient history", isCorrect: false },
                    { id: "b", text: "The text is about modern education", isCorrect: true },
                    { id: "c", text: "The text focuses on cooking", isCorrect: false },
                    { id: "d", text: "The text describes a journey", isCorrect: false }
                  ]
                }
              ]
            };
            
            console.log('Using fallback data:', fallbackData);
            setExerciseData(fallbackData);
            setError("API returned invalid JSON, using test data instead.");
          }
        }
      } else {
        console.error('No content in OpenAI response');
        setError("No result from OpenAI");
      }
    } catch (e) {
      console.error("Error in handleGenerate:", e);
      setError("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = () => {
    if (template.label === "Create ABCD Questions for a Text" && exerciseData) {
      if (currentTab > 0 && onInsertToBoard) {
        onInsertToBoard(exerciseData, 'abcd');
      } else if (editor) {
        editor.chain().focus().insertContent({ type: 'abcdExercise', attrs: { questions: exerciseData.questions, title: exerciseData.title, level: exerciseData.level } }).run();
      }
    } else if (result) {
      if (currentTab > 0 && onInsertToBoard) {
        onInsertToBoard(result, 'text');
      } else if (editor) {
        editor.chain().focus().insertContent(result).run();
      }
    }
    onClose();
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.18)", zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 32px #90caf944", padding: 32, minWidth: 420, maxWidth: 540, width: "100%", display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 22 }}>AI Assistant</h2>
          <button onClick={onClose} style={{ fontSize: 22, background: 'none', border: 'none', cursor: 'pointer', color: '#90caf9' }}>×</button>
        </div>
        <label style={{ fontWeight: 600, fontSize: 15 }}>Template:</label>
        <select value={template.label} onChange={e => setTemplate(TEMPLATES.find(t => t.label === e.target.value))} style={{ fontSize: 16, padding: 8, borderRadius: 8, border: '1.5px solid #90caf9', background: '#e3f2fd' }}>
          {TEMPLATES.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
        </select>
        {template.label === "Fill in the Gap" ? (
          <>
            <label style={{ fontWeight: 600, fontSize: 15 }}>Words/Phrases (comma or newline separated):</label>
            <textarea
              value={phrasesInput}
              onChange={e => setPhrasesInput(e.target.value)}
              rows={5}
              style={{ fontSize: 16, padding: 10, borderRadius: 8, border: '1.5px solid #90caf9', background: '#f7fbff', resize: 'vertical' }}
              placeholder="word1, word2\nphrase one\nphrase two"
            />
            <label style={{ fontWeight: 600, fontSize: 15 }}>CEFR Level:</label>
            <select value={gapLevel} onChange={e => setGapLevel(e.target.value)} style={{ fontSize: 16, padding: 8, borderRadius: 8, border: '1.5px solid #90caf9', background: '#e3f2fd' }}>
              <option value="A1">A1 - Beginner</option>
              <option value="A2">A2 - Elementary</option>
              <option value="B1">B1 - Intermediate</option>
              <option value="B2">B2 - Upper Intermediate</option>
              <option value="C1">C1 - Advanced</option>
              <option value="C2">C2 - Mastery</option>
            </select>
          </>
        ) : (
          <>
            <label style={{ fontWeight: 600, fontSize: 15 }}>Text / Topic / Words:</label>
            <textarea value={input} onChange={e => setInput(e.target.value)} rows={5} style={{ fontSize: 16, padding: 10, borderRadius: 8, border: '1.5px solid #90caf9', background: '#f7fbff', resize: 'vertical' }} placeholder="Paste your text, topic, or words here..." />
          </>
        )}
        
        {template.label === "Create ABCD Questions for a Text" && (
          <>
            <label style={{ fontWeight: 600, fontSize: 15 }}>CEFR Level:</label>
            <select value={level} onChange={e => setLevel(e.target.value)} style={{ fontSize: 16, padding: 8, borderRadius: 8, border: '1.5px solid #90caf9', background: '#e3f2fd' }}>
              <option value="A1">A1 - Beginner</option>
              <option value="A2">A2 - Elementary</option>
              <option value="B1">B1 - Intermediate</option>
              <option value="B2">B2 - Upper Intermediate</option>
              <option value="C1">C1 - Advanced</option>
              <option value="C2">C2 - Mastery</option>
            </select>
          </>
        )}
        
        <button
          onClick={handleGenerate}
          disabled={loading || (template.label === "Fill in the Gap" ? !phrasesInput.trim() : !input)}
          style={{ fontSize: 17, padding: '10px 0', borderRadius: 8, border: 'none', background: '#90caf9', color: '#fff', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 8 }}
        >
          {loading ? 'Generating...' : template.label === "Create ABCD Questions for a Text" ? 'Do the magic' : 'Generate'}
        </button>
        {loading && <div style={{ color: '#90caf9', fontWeight: 600 }}>Loading...</div>}
        {error && <div style={{ color: 'red', fontWeight: 600 }}>{error}</div>}
        {result && (
          <>
            {template.label === "Create ABCD Questions for a Text" && exerciseData ? (
              <>
                <label style={{ fontWeight: 600, fontSize: 15 }}>Generated ABCD Exercise:</label>
                <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, border: '1px solid #e9ecef', maxHeight: 300, overflowY: 'auto' }}
                onWheel={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  // Позволяем прокрутку внутри списка
                  const target = e.currentTarget;
                  const scrollTop = target.scrollTop;
                  const scrollHeight = target.scrollHeight;
                  const clientHeight = target.clientHeight;
                  
                  if (e.deltaY > 0 && scrollTop < scrollHeight - clientHeight) {
                    // Прокрутка вниз
                    target.scrollTop += 20;
                  } else if (e.deltaY < 0 && scrollTop > 0) {
                    // Прокрутка вверх
                    target.scrollTop -= 20;
                  }
                }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: 18, color: '#22223b' }}>{exerciseData.title}</h3>
                  <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#6c757d' }}>Level: {exerciseData.level}</p>
                  {exerciseData.questions && exerciseData.questions.map((q, index) => (
                    <div key={index} style={{ marginBottom: 16, padding: 12, background: '#fff', borderRadius: 6, border: '1px solid #dee2e6' }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 600, fontSize: 14 }}>{index + 1}. {q.question}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {q.options && q.options.map((option, optIndex) => (
                          <div key={optIndex} style={{ 
                            padding: '6px 8px', 
                            borderRadius: 4, 
                            background: option.isCorrect ? '#d4edda' : '#f8f9fa',
                            border: option.isCorrect ? '1px solid #c3e6cb' : '1px solid #e9ecef',
                            fontSize: 13
                          }}>
                            <strong>{option.id}.</strong> {option.text}
                            {option.isCorrect && <span style={{ color: '#155724', marginLeft: 8 }}>✓ Correct</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={handleInsert} style={{ fontSize: 16, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#8a2be2', color: '#fff', fontWeight: 700, cursor: 'pointer', flex: 1 }}>
                    Add
                  </button>
                </div>
              </>
            ) : (
              <>
                <label style={{ fontWeight: 600, fontSize: 15 }}>Result:</label>
                <textarea value={result} readOnly rows={8} style={{ fontSize: 15, padding: 10, borderRadius: 8, border: '1.5px solid #90caf9', background: '#f7fbff', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={handleInsert} style={{ fontSize: 16, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#8a2be2', color: '#fff', fontWeight: 700, cursor: 'pointer', flex: 1 }}>
                    Add
                  </button>
                </div>
              </>
            )}
            {result && template.label === "Fill in the Gap" && (
              <>
                <label style={{ fontWeight: 600, fontSize: 15 }}>Generated Fill in the Gap Exercise:</label>
                <FillGapEditor
                  initialData={result}
                  onChange={setExerciseData}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FillGapEditor({ initialData, onChange }) {
  // Парсим initialData через useMemo
  const parsed = React.useMemo(() => {
    try {
      if (initialData) {
        return typeof initialData === 'string' ? JSON.parse(initialData) : initialData;
      }
    } catch (e) {
      return { parseError: e };
    }
    return { title: '', level: '', text: '', gaps: [] };
  }, [initialData]);

  const [title, setTitle] = useState(parsed.title || 'Fill in the Gaps');
  const [level, setLevel] = useState(parsed.level || 'B1');
  const [sentences, setSentences] = useState(() => {
    return parsed.text
      ? parsed.text.split(/\n|(?<=\d\.) /).map(s => s.trim()).filter(Boolean)
      : [];
  });
  const [gaps, setGaps] = useState(parsed.gaps || []);

  useEffect(() => {
    onChange && onChange({ title, level, sentences, gaps });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, level, sentences, gaps]);

  if (parsed.parseError) {
    return <div style={{ color: 'red' }}>Invalid JSON from AI</div>;
  }

  // Редактирование предложения
  const handleSentenceChange = (i, value) => {
    setSentences(prev => prev.map((s, idx) => idx === i ? value : s));
  };
  // Редактирование gap
  const handleGapChange = (i, value) => {
    setGaps(prev => prev.map((g, idx) => idx === i ? { ...g, word: value } : g));
  };
  // Добавить новое предложение
  const handleAdd = () => {
    setSentences(prev => [...prev, '']);
    setGaps(prev => [...prev, { id: `gap_${Date.now()}`, word: '' }]);
  };
  // Удалить предложение
  const handleDelete = (i) => {
    setSentences(prev => prev.filter((_, idx) => idx !== i));
    setGaps(prev => prev.filter((_, idx) => idx !== i));
  };

  return (
    <div style={{ border: '1.5px solid #e3e3e3', borderRadius: 10, padding: 16, marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" style={{ flex: 1, fontWeight: 600, fontSize: 16, padding: 6, borderRadius: 6, border: '1.5px solid #90caf9' }} />
        <select value={level} onChange={e => setLevel(e.target.value)} style={{ fontSize: 15, padding: 6, borderRadius: 6, border: '1.5px solid #90caf9' }}>
          <option value="A1">A1</option>
          <option value="A2">A2</option>
          <option value="B1">B1</option>
          <option value="B2">B2</option>
          <option value="C1">C1</option>
          <option value="C2">C2</option>
        </select>
      </div>
      {sentences.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input
            value={s}
            onChange={e => handleSentenceChange(i, e.target.value)}
            style={{ flex: 3, padding: 6, borderRadius: 6, border: '1.5px solid #90caf9', fontSize: 15 }}
            placeholder={`Sentence ${i + 1}`}
          />
          <input
            value={gaps[i]?.word || ''}
            onChange={e => handleGapChange(i, e.target.value)}
            style={{ flex: 1, padding: 6, borderRadius: 6, border: '1.5px solid #90caf9', fontSize: 15 }}
            placeholder="Gap word/phrase"
          />
          <button onClick={() => handleDelete(i)} style={{ color: '#dc3545', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
      ))}
      <button onClick={handleAdd} style={{ marginTop: 8, background: '#90caf9', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>+ Add Sentence</button>
    </div>
  );
} 